import { useState, useRef, useEffect } from "react";
import { playChord, playProgression, stopAll, preloadPiano } from "./audio";
import { getSuggestions, getInversionNotes, inversionName, inversionShortName, recommendInversion } from "./suggestions";
import ScaleNotation from "./ScaleNotation";

// ─── Music Data ───────────────────────────────────────────────────────────────

const CIRCLE = [
  { key:"C",  major:"C",  minor:"Am",  sharps:0  },
  { key:"G",  major:"G",  minor:"Em",  sharps:1  },
  { key:"D",  major:"D",  minor:"Bm",  sharps:2  },
  { key:"A",  major:"A",  minor:"F#m", sharps:3  },
  { key:"E",  major:"E",  minor:"C#m", sharps:4  },
  { key:"B",  major:"B",  minor:"G#m", sharps:5  },
  { key:"F#", major:"F#", minor:"D#m", sharps:6  },
  { key:"Db", major:"Db", minor:"Bbm", sharps:-5 },
  { key:"Ab", major:"Ab", minor:"Fm",  sharps:-4 },
  { key:"Eb", major:"Eb", minor:"Cm",  sharps:-3 },
  { key:"Bb", major:"Bb", minor:"Gm",  sharps:-2 },
  { key:"F",  major:"F",  minor:"Dm",  sharps:-1 },
];

const NOTES_SHARP = ["C","C#","D","D#","E","F","F#","G","G#","A","A#","B"];
const NOTES_FLAT  = ["C","Db","D","Eb","E","F","Gb","G","Ab","A","Bb","B"];
const DEG         = ["I","II","III","IV","V","VI","VII"];
const MAJ_TYPES   = ["maj","min","min","maj","maj","min","dim"];
const MIN_TYPES   = ["min","dim","maj","min","min","maj","maj"];
const FN_MAJ      = ["Tonika","Supertonica","Mediante","Subdominante","Dominante","Submediane","Leitton"];
const FN_MIN      = ["Tonika","Supertonica","Mediante","Subdominante","Dominante","Submediane","Subtonica"];
const MAJ_7TH     = ["maj7","min7","min7","maj7","dom7","min7","m7b5"];
const MIN_7TH     = ["min7","m7b5","maj7","min7","min7","maj7","dom7"];

const FC = {
  Tonika:      { bg:"#e8f5e9", bd:"#81c784", tx:"#2e7d32" },
  Subdominante:{ bg:"#e3f2fd", bd:"#64b5f6", tx:"#1565c0" },
  Dominante:   { bg:"#fff8e1", bd:"#ffd54f", tx:"#e65100" },
  Supertonica: { bg:"#f1f8e9", bd:"#aed581", tx:"#558b2f" },
  Mediante:    { bg:"#ede7f6", bd:"#ba68c8", tx:"#6a1b9a" },
  Submediane:  { bg:"#fce4ec", bd:"#f06292", tx:"#880e4f" },
  Leitton:     { bg:"#fbe9e7", bd:"#ff8a65", tx:"#bf360c" },
  Subtonica:   { bg:"#fbe9e7", bd:"#ff8a65", tx:"#bf360c" },
};

// ─── Music Helpers ────────────────────────────────────────────────────────────

function pickNotes(sharps) { return sharps >= 0 ? NOTES_SHARP : NOTES_FLAT; }

function buildScale(root, isMinor, sharps) {
  const notes = pickNotes(sharps);
  let ri = notes.indexOf(root);
  if (ri === -1) {
    const alt = sharps >= 0 ? NOTES_FLAT : NOTES_SHARP;
    ri = alt.indexOf(root);
    if (ri === -1) return [];
    return scaleFrom(ri, isMinor, alt);
  }
  return scaleFrom(ri, isMinor, notes);
}
function scaleFrom(ri, isMinor, notes) {
  const steps = isMinor ? [2,1,2,2,1,2,2] : [2,2,1,2,2,2,1];
  const s = [notes[ri]]; let cur = ri;
  for (const st of steps.slice(0,6)) { cur=(cur+st)%12; s.push(notes[cur]); }
  return s;
}
function buildChords(scale, isMinor, use7th) {
  const types  = isMinor ? MIN_TYPES : MAJ_TYPES;
  const types7 = isMinor ? MIN_7TH   : MAJ_7TH;
  const fns    = isMinor ? FN_MIN    : FN_MAJ;
  return scale.map((note,i) => ({
    degree: DEG[i], root: note, type: types[i], type7: types7[i],
    label: use7th ? make7Label(note,types7[i]) : (note+(types[i]==="maj"?"":types[i]==="min"?"m":"°")),
    label3: note+(types[i]==="maj"?"":types[i]==="min"?"m":"°"),
    label7: make7Label(note,types7[i]),
    fn: fns[i], use7th,
  }));
}
function make7Label(root,type7){
  switch(type7){
    case"maj7": return root+"maj7";
    case"dom7": return root+"7";
    case"min7": return root+"m7";
    case"m7b5": return root+"ø7";
    case"dim7": return root+"°7";
    default:    return root+type7;
  }
}

// Root-position notes (ascending)
function getRootNotes(root, type, type7, use7th, sharps) {
  const notes = pickNotes(sharps);
  let ri = notes.indexOf(root);
  if (ri === -1) {
    const alt = sharps>=0 ? NOTES_FLAT : NOTES_SHARP;
    ri = alt.indexOf(root);
    if (ri === -1) return [root];
    return use7th ? build7th(ri,type7,alt) : buildTriad(ri,type,alt);
  }
  return use7th ? build7th(ri,type7,notes) : buildTriad(ri,type,notes);
}
function buildTriad(ri,type,n){
  if(type==="maj") return [n[ri],n[(ri+4)%12],n[(ri+7)%12]];
  if(type==="min") return [n[ri],n[(ri+3)%12],n[(ri+7)%12]];
  return [n[ri],n[(ri+3)%12],n[(ri+6)%12]];
}
function build7th(ri,type7,n){
  switch(type7){
    case"maj7": return [n[ri],n[(ri+4)%12],n[(ri+7)%12],n[(ri+11)%12]];
    case"dom7": return [n[ri],n[(ri+4)%12],n[(ri+7)%12],n[(ri+10)%12]];
    case"min7": return [n[ri],n[(ri+3)%12],n[(ri+7)%12],n[(ri+10)%12]];
    case"m7b5": return [n[ri],n[(ri+3)%12],n[(ri+6)%12],n[(ri+10)%12]];
    case"dim7": return [n[ri],n[(ri+3)%12],n[(ri+6)%12],n[(ri+9)%12]];
    default:    return [n[ri],n[(ri+4)%12],n[(ri+7)%12],n[(ri+10)%12]];
  }
}

// Get notes for a chord object including inversion
function getChordNotes(chord, sharps) {
  const root = getRootNotes(chord.root, chord.type, chord.type7, chord.use7th, sharps);
  const inv  = chord.inversion || 0;
  return getInversionNotes(root, inv);
}

// ─── Tiny Piano ───────────────────────────────────────────────────────────────

const SEMI_TO_WPOS = [0,0,1,1,2,3,3,4,4,5,5,6];
const WPOS_TO_SEMI = [0,2,4,5,7,9,11];
const WHITE_HAS_BLK = [true,true,false,true,true,true,false];

function noteIdx2(n){const a=NOTES_SHARP.indexOf(n);if(a>=0)return a;return NOTES_FLAT.indexOf(n);}
function semiToAbsW(s){return Math.floor(s/12)*7+SEMI_TO_WPOS[s%12];}

function TinyPiano({ notes, color }) {
  if (!notes||notes.length===0) return null;
  const rootSemi = noteIdx2(notes[0]);
  if (rootSemi<0) return null;
  const semis=[rootSemi]; let prev=rootSemi;
  for(let i=1;i<notes.length;i++){let s=noteIdx2(notes[i]);if(s<0)continue;while(s<=prev)s+=12;semis.push(s);prev=s;}
  const sw=semiToAbsW(semis[0]), lw=semiToAbsW(semis[semis.length-1]);
  const ew=Math.max(lw+1,sw+6), nw=ew-sw+1;
  const ww=14,wh=34,bw=9,bh=21,totalW=nw*ww;
  const act=new Set(semis.map(s=>s%12));
  const wkeys=[];
  for(let w=sw;w<=ew;w++){const oct=Math.floor(w/7),wInOct=w%7;const abs=oct*12+WPOS_TO_SEMI[wInOct];wkeys.push({abs,sioc:abs%12,xPos:(w-sw)*ww,wInOct});}
  return(
    <svg width={totalW} height={wh} style={{display:"block",borderRadius:3}}>
      {wkeys.map((k,i)=><rect key={"w"+i} x={k.xPos} y={0} width={ww-1} height={wh} rx={2} fill={act.has(k.sioc)?color:"#fff"} stroke="#ddd" strokeWidth={0.5}/>)}
      {wkeys.map((k,i)=>{if(!WHITE_HAS_BLK[k.wInOct])return null;const bs=(k.sioc+1)%12;const bx=k.xPos+ww-bw/2;if(bx+bw>totalW)return null;return<rect key={"b"+i} x={bx} y={0} width={bw} height={bh} rx={1} fill={act.has(bs)?color:"#2a2a2a"}/>;})}
      {wkeys.map((k,i)=>{if(!act.has(k.sioc))return null;const n=notes.find(x=>noteIdx2(x)%12===k.sioc)||"";return<text key={"wl"+i} x={k.xPos+ww/2-.5} y={wh-4} textAnchor="middle" fontSize={5.5} fontWeight="700" fill="#fff" fontFamily="Inter,sans-serif">{n}</text>;})}
      {wkeys.map((k,i)=>{if(!WHITE_HAS_BLK[k.wInOct])return null;const bs=(k.sioc+1)%12;if(!act.has(bs))return null;const bx=k.xPos+ww-bw/2;if(bx+bw>totalW)return null;const n=notes.find(x=>noteIdx2(x)%12===bs)||"";return<text key={"bl"+i} x={bx+bw/2} y={bh-3} textAnchor="middle" fontSize={5} fontWeight="700" fill="#fff" fontFamily="Inter,sans-serif">{n}</text>;})}
    </svg>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const S = {
  pill: { padding:"5px 12px", borderRadius:20, border:"none", cursor:"pointer", fontFamily:"Inter,sans-serif", fontSize:11, fontWeight:600, transition:"all 0.15s" },
  card: { borderRadius:12, border:"1px solid #ebebeb", background:"#fafafa", padding:"12px 14px" },
};

// ─── Key Selector ─────────────────────────────────────────────────────────────

function KeySelector({ sel, mode, onSelect, onMode }) {
  return (
    <div style={{ marginBottom:16 }}>
      <div style={{ fontSize:10, color:"#bbb", fontFamily:"Inter,sans-serif", fontWeight:700, letterSpacing:"0.06em", marginBottom:8 }}>TONART</div>
      <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:8 }}>
        {CIRCLE.map(c => (
          <button key={c.key} onClick={()=>onSelect(c)} style={{
            ...S.pill, fontSize:12, padding:"5px 11px",
            background:sel?.key===c.key?"#1a1a1a":"#f0f0f0",
            color:sel?.key===c.key?"#fff":"#666",
          }}>{mode==="major"?c.major:c.minor}</button>
        ))}
      </div>
      <div style={{ display:"flex", gap:6 }}>
        {["major","minor"].map(m=>(
          <button key={m} onClick={()=>onMode(m)} style={{
            ...S.pill, fontSize:10,
            background:mode===m?"#444":"#f0f0f0",
            color:mode===m?"#fff":"#888",
          }}>{m==="major"?"DUR":"MOLL"}</button>
        ))}
      </div>
    </div>
  );
}

// ─── Scale Chord Button ───────────────────────────────────────────────────────

function ScaleChordBtn({ chord, sharps, onClick }) {
  const fc = FC[chord.fn]||FC.Tonika;
  const rootNotes = getRootNotes(chord.root,chord.type,chord.type7,chord.use7th,sharps);
  return (
    <button onClick={onClick} style={{
      display:"flex", flexDirection:"column", alignItems:"center", gap:3,
      padding:"9px 10px", borderRadius:11, minWidth:62,
      background:fc.bg, border:`1.5px solid ${fc.bd}`,
      cursor:"pointer", transition:"all 0.12s", fontFamily:"Inter,sans-serif",
    }}>
      <div style={{ fontSize:13, fontWeight:700, color:fc.tx, lineHeight:1 }}>{chord.label}</div>
      <div style={{ fontSize:8, color:fc.bd, fontWeight:600 }}>{chord.degree}</div>
      <div style={{ display:"flex", gap:2, flexWrap:"wrap", justifyContent:"center" }}>
        {rootNotes.map((n,i)=>(
          <span key={i} style={{ fontSize:8, padding:"1px 4px", borderRadius:4, background:"rgba(255,255,255,0.7)", color:fc.tx, fontWeight:600 }}>{n}</span>
        ))}
      </div>
    </button>
  );
}

// ─── Inversion Picker ────────────────────────────────────────────────────────

function InversionPicker({ chord, sharps, currentInv, onChange }) {
  const fc = FC[chord.fn]||FC.Tonika;
  const rootNotes = getRootNotes(chord.root,chord.type,chord.type7,chord.use7th,sharps);
  const count = rootNotes.length;
  return (
    <div style={{ marginTop:8, paddingTop:8, borderTop:"1px solid " + fc.bd }}>
      <div style={{ fontSize:9, color:fc.bd, fontFamily:"Inter,sans-serif", fontWeight:700, letterSpacing:"0.05em", marginBottom:6 }}>UMKEHRUNG</div>
      <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
        {Array.from({length:count},(_,i)=>{
          const invNotes = getInversionNotes(rootNotes,i);
          const active = currentInv===i;
          return (
            <button key={i} onClick={()=>onChange(i)} style={{
              display:"flex", flexDirection:"column", alignItems:"center", gap:2,
              padding:"4px 7px", borderRadius:7,
              background:active?"#1a1a1a":fc.bg,
              border:`1px solid ${active?"#1a1a1a":fc.bd}`,
              cursor:"pointer", fontFamily:"Inter,sans-serif",
            }}>
              <div style={{ fontSize:8, fontWeight:700, color:active?"#fff":fc.tx }}>{inversionShortName(i)}</div>
              <div style={{ display:"flex", gap:1 }}>
                {invNotes.map((n,j)=>(
                  <span key={j} style={{ fontSize:7, color:active?"#ccc":fc.bd }}>{n}{j<invNotes.length-1?"-":""}</span>
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Chord Pill in Progression ────────────────────────────────────────────────

function ChordPill({ chord, sharps, isPlaying, expanded, onPlay, onRemove, onMoveLeft, onMoveRight, onToggleExpand, onChangeInv, isFirst, isLast }) {
  const fc    = FC[chord.fn]||FC.Tonika;
  const notes = getChordNotes(chord, sharps);
  const inv   = chord.inversion||0;
  const rootNotes = getRootNotes(chord.root,chord.type,chord.type7,chord.use7th,sharps);

  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
      {/* Main card */}
      <div style={{
        borderRadius:12, minWidth:70, textAlign:"center",
        background:isPlaying?"#1a1a1a":fc.bg,
        border:`2px solid ${isPlaying?"#1a1a1a":fc.bd}`,
        boxShadow:isPlaying?"0 4px 14px rgba(0,0,0,0.18)":"none",
        overflow:"hidden", transition:"all 0.15s",
      }}>
        {/* Clickable area for play */}
        <div onClick={onPlay} style={{ padding:"8px 10px 6px", cursor:"pointer" }}>
          <div style={{ fontSize:14, fontWeight:700, color:isPlaying?"#fff":fc.tx, fontFamily:"Inter,sans-serif", lineHeight:1 }}>{chord.label}</div>
          <div style={{ fontSize:8, color:isPlaying?"#aaa":fc.bd, fontFamily:"Inter,sans-serif", marginTop:1 }}>
            {chord.degree} · {inversionShortName(inv)}
          </div>
          <div style={{ display:"flex", gap:2, justifyContent:"center", marginTop:4, flexWrap:"wrap" }}>
            {notes.map((n,i)=>(
              <span key={i} style={{ fontSize:7, padding:"1px 3px", borderRadius:3, background:isPlaying?"rgba(255,255,255,0.12)":"rgba(255,255,255,0.7)", color:isPlaying?"#ccc":fc.tx, fontWeight:700, fontFamily:"Inter,sans-serif" }}>{n}</span>
            ))}
          </div>
          <div style={{ marginTop:5 }}>
            <TinyPiano notes={notes} color={isPlaying?"#81c784":fc.bd}/>
          </div>
          {isPlaying&&<div style={{ fontSize:8, color:"#81c784", fontFamily:"Inter,sans-serif", marginTop:3 }}>▶</div>}
        </div>
        {/* Expand button for inversion picker */}
        <div onClick={onToggleExpand} style={{ background:isPlaying?"rgba(255,255,255,0.05)":"rgba(0,0,0,0.04)", padding:"3px", cursor:"pointer", fontSize:8, color:isPlaying?"#888":fc.bd, fontFamily:"Inter,sans-serif" }}>
          {expanded?"▲ weniger":"▼ Umkehrung"}
        </div>
        {/* Inversion picker */}
        {expanded && !isPlaying && (
          <div style={{ padding:"0 8px 8px" }}>
            <InversionPicker chord={chord} sharps={sharps} currentInv={inv} onChange={onChangeInv}/>
          </div>
        )}
      </div>
      {/* Controls */}
      <div style={{ display:"flex", gap:2 }}>
        <button onClick={onMoveLeft} disabled={isFirst} style={{ width:18,height:18,borderRadius:4,border:"none",cursor:isFirst?"default":"pointer",background:isFirst?"#f5f5f5":"#e8e8e8",fontSize:10,color:isFirst?"#ddd":"#888",display:"flex",alignItems:"center",justifyContent:"center" }}>‹</button>
        <button onClick={onRemove}                       style={{ width:18,height:18,borderRadius:4,border:"none",cursor:"pointer",background:"#fee",fontSize:10,color:"#e88",display:"flex",alignItems:"center",justifyContent:"center" }}>×</button>
        <button onClick={onMoveRight} disabled={isLast}  style={{ width:18,height:18,borderRadius:4,border:"none",cursor:isLast?"default":"pointer",background:isLast?"#f5f5f5":"#e8e8e8",fontSize:10,color:isLast?"#ddd":"#888",display:"flex",alignItems:"center",justifyContent:"center" }}>›</button>
      </div>
    </div>
  );
}

// ─── Suggestion Card ──────────────────────────────────────────────────────────

function SuggestionCard({ suggestion, sharps, use7th, prevNotes, onAdd, onPreview }) {
  const chord = suggestion.chord;
  const fc    = FC[chord.fn]||FC.Tonika;
  const conf  = Math.round(suggestion.confidence*100);

  // Get root notes and recommended inversion
  const rootNotes = getRootNotes(chord.root,chord.type,chord.type7,use7th,sharps);
  const rec       = recommendInversion(prevNotes, rootNotes);
  const invNotes  = getInversionNotes(rootNotes, rec.inversion);

  const label = use7th ? chord.label7 : chord.label3;

  return (
    <div style={{ ...S.card, border:`1px solid ${fc.bd}`, background:fc.bg, display:"flex", alignItems:"flex-start", gap:10 }}>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4 }}>
          <div style={{ fontFamily:"Inter,sans-serif", fontSize:13, fontWeight:700, color:fc.tx }}>{label}</div>
          {rec.inversion>0&&(
            <span style={{ fontSize:9, padding:"1px 6px", borderRadius:8, background:fc.bd, color:"#fff", fontFamily:"Inter,sans-serif", fontWeight:700 }}>
              {inversionName(rec.inversion, rootNotes.length)}
            </span>
          )}
        </div>
        {/* Notes row */}
        <div style={{ display:"flex", gap:3, marginBottom:4, flexWrap:"wrap" }}>
          {invNotes.map((n,i)=>(
            <span key={i} style={{ fontSize:9, padding:"1px 5px", borderRadius:4, background:"rgba(255,255,255,0.7)", color:fc.tx, fontWeight:600, fontFamily:"Inter,sans-serif" }}>{n}</span>
          ))}
        </div>
        {/* Mini piano */}
        <div style={{ marginBottom:4 }}>
          <TinyPiano notes={invNotes} color={fc.bd}/>
        </div>
        <div style={{ fontFamily:"Inter,sans-serif", fontSize:10, color:fc.tx, opacity:0.75, lineHeight:1.4 }}>{suggestion.reason}</div>
        {rec.inversion>0&&(
          <div style={{ fontFamily:"Inter,sans-serif", fontSize:9, color:fc.bd, marginTop:2, fontStyle:"italic" }}>↪ {rec.reason}</div>
        )}
        {suggestion.pattern&&(
          <div style={{ marginTop:4, fontSize:9, padding:"1px 6px", borderRadius:8, background:fc.bd, color:"#fff", display:"inline-block", fontFamily:"Inter,sans-serif", fontWeight:700 }}>{suggestion.pattern}</div>
        )}
      </div>
      <div style={{ textAlign:"right", flexShrink:0 }}>
        <div style={{ fontSize:10, color:fc.bd, fontFamily:"Inter,sans-serif", fontWeight:700, marginBottom:6 }}>{conf}%</div>
        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
          <button onClick={onPreview} style={{ ...S.pill, background:"#fff", color:fc.tx, border:`1px solid ${fc.bd}`, padding:"5px 9px", fontSize:10 }}>▶</button>
          <button onClick={onAdd}     style={{ ...S.pill, background:fc.bd, color:"#fff", padding:"5px 9px", fontSize:10 }}>+ {inversionShortName(rec.inversion)}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Saved List ───────────────────────────────────────────────────────────────

function SavedList({ saved, onLoad, onDelete }) {
  if (saved.length===0) return (
    <div style={{ fontSize:11,color:"#ccc",fontFamily:"Inter,sans-serif",textAlign:"center",padding:"24px 0" }}>Noch keine gespeicherten Progressionen</div>
  );
  return (
    <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
      {saved.map((item,i)=>(
        <div key={i} style={{ ...S.card,display:"flex",alignItems:"center",gap:10 }}>
          <div style={{ flex:1,minWidth:0 }}>
            <div style={{ fontFamily:"Inter,sans-serif",fontSize:12,fontWeight:600,color:"#333" }}>{item.name}</div>
            <div style={{ fontFamily:"Inter,sans-serif",fontSize:10,color:"#bbb",marginTop:1 }}>{item.key} {item.mode==="major"?"Dur":"Moll"} · {item.chords.length} Akkorde</div>
            <div style={{ display:"flex",gap:4,marginTop:6,flexWrap:"wrap" }}>
              {item.chords.map((c,j)=>{const fc=FC[c.fn]||FC.Tonika;return<span key={j} style={{ fontSize:10,padding:"1px 6px",borderRadius:6,background:fc.bg,color:fc.tx,border:`1px solid ${fc.bd}`,fontFamily:"Inter,sans-serif",fontWeight:600 }}>{c.label}</span>;})}
            </div>
          </div>
          <div style={{ display:"flex",flexDirection:"column",gap:4,flexShrink:0 }}>
            <button onClick={()=>onLoad(item)} style={{ ...S.pill,background:"#f0f0f0",color:"#555",padding:"4px 10px",fontSize:10 }}>Laden</button>
            <button onClick={()=>onDelete(i)}  style={{ ...S.pill,background:"#fee",color:"#e88",padding:"4px 10px",fontSize:10 }}>Löschen</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Metronome ────────────────────────────────────────────────────────────────

function Metronome({ bpm, onBpmChange }) {
  const [running,      setRunning]      = useState(false);
  const [beat,         setBeat]         = useState(-1);
  const [beatsPerBar,  setBeatsPerBar]  = useState(4);

  const audioCtxRef  = useRef(null);
  const timerRef     = useRef(null);
  const nextBeatTime = useRef(0);
  const beatCount    = useRef(0);
  const bpmRef       = useRef(bpm);
  const bpbRef       = useRef(beatsPerBar);
  const tapTimes     = useRef([]);

  // Keep refs in sync
  useEffect(() => { bpmRef.current = bpm; }, [bpm]);
  useEffect(() => { bpbRef.current = beatsPerBar; }, [beatsPerBar]);
  useEffect(() => () => clearInterval(timerRef.current), []);

  function getCtx() {
    if (!audioCtxRef.current)
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return audioCtxRef.current;
  }

  function click(time, accent) {
    const ctx = getCtx();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "square";
    osc.frequency.value = accent ? 1200 : 900;
    gain.gain.setValueAtTime(accent ? 0.45 : 0.2, time);
    gain.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
    osc.start(time);
    osc.stop(time + 0.055);
  }

  function schedule() {
    const ctx = getCtx();
    while (nextBeatTime.current < ctx.currentTime + 0.12) {
      const b = beatCount.current % bpbRef.current;
      click(nextBeatTime.current, b === 0);
      const delay = Math.max(0, (nextBeatTime.current - ctx.currentTime) * 1000);
      const cb = b;
      setTimeout(() => setBeat(cb), delay);
      beatCount.current++;
      nextBeatTime.current += 60 / bpmRef.current;
    }
  }

  function startMetronome() {
    const ctx = getCtx();
    if (ctx.state === "suspended") ctx.resume();
    beatCount.current = 0;
    nextBeatTime.current = ctx.currentTime + 0.05;
    timerRef.current = setInterval(schedule, 25);
    setRunning(true);
  }

  function stopMetronome() {
    clearInterval(timerRef.current);
    setRunning(false);
    setBeat(-1);
  }

  function handleTap() {
    const now = Date.now();
    if (tapTimes.current.length > 0 && now - tapTimes.current[tapTimes.current.length - 1] > 2500)
      tapTimes.current = [];
    tapTimes.current.push(now);
    if (tapTimes.current.length > 4) tapTimes.current.shift();
    if (tapTimes.current.length >= 2) {
      const intervals = [];
      for (let i = 1; i < tapTimes.current.length; i++)
        intervals.push(tapTimes.current[i] - tapTimes.current[i - 1]);
      const avg = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      onBpmChange(Math.min(220, Math.max(40, Math.round(60000 / avg))));
    }
  }

  return (
    <div style={{ ...S.card }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
        <div style={{ fontSize:10, color:"#bbb", fontFamily:"Inter,sans-serif", fontWeight:700, letterSpacing:"0.06em" }}>METRONOM</div>
        <div style={{ display:"flex", gap:4 }}>
          {[3,4,6].map(n => (
            <button key={n} onClick={() => { setBeatsPerBar(n); setBeat(-1); beatCount.current = 0; }} style={{
              ...S.pill, padding:"2px 8px", fontSize:9,
              background: beatsPerBar===n ? "#444" : "#f0f0f0",
              color: beatsPerBar===n ? "#fff" : "#888",
            }}>{n}/4</button>
          ))}
        </div>
      </div>

      {/* Beat visualizer */}
      <div style={{ display:"flex", gap:6, justifyContent:"center", alignItems:"center", marginBottom:12 }}>
        {Array.from({ length: beatsPerBar }, (_, i) => (
          <div key={i} style={{
            width: i === 0 ? 14 : 10,
            height: i === 0 ? 14 : 10,
            borderRadius: "50%",
            background: running && beat === i
              ? (i === 0 ? "#1a1a1a" : "#666")
              : (i === 0 ? "#d8d8d8" : "#ebebeb"),
            transition: "background 0.04s",
          }}/>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
        <button onClick={running ? stopMetronome : startMetronome} style={{
          ...S.pill, background: running ? "#fee" : "#1a1a1a",
          color: running ? "#e88" : "#fff", padding:"7px 14px", fontSize:11, flexShrink:0,
        }}>
          {running ? "⏹ Stop" : "▶ Start"}
        </button>
        <button onClick={handleTap} style={{
          ...S.pill, background:"#f0f0f0", color:"#555",
          padding:"7px 12px", fontSize:10, flexShrink:0,
        }}>TAP</button>
        <input type="range" min={40} max={220} value={bpm}
          onChange={e => onBpmChange(Number(e.target.value))}
          style={{ flex:1, accentColor:"#1a1a1a" }}/>
        <span style={{ fontSize:12, fontFamily:"Inter,sans-serif", fontWeight:700, color:"#333", width:28, textAlign:"right", flexShrink:0 }}>{bpm}</span>
        <span style={{ fontSize:9, fontFamily:"Inter,sans-serif", color:"#bbb", flexShrink:0 }}>BPM</span>
      </div>
    </div>
  );
}

// ─── Staff Notation ───────────────────────────────────────────────────────────

// Diatonic letter → step within octave (C=0 … B=6)
const LETTER_STEP = { C:0, D:1, E:2, F:3, G:4, A:5, B:6 };
const E4_ABS = 4 * 7 + 2; // = 30  (absolute diatonic step for E4 = treble first line)

// Returns staff relSteps for ascending root-position chord notes.
// relStep 0 = E4 (first line), positive = up, negative = below staff.
function getStaffSteps(notes) {
  let prevAbs = -Infinity;
  return notes.map(n => {
    const ls = LETTER_STEP[n[0]] ?? 0;
    let abs = 4 * 7 + ls; // start in octave 4
    while (abs <= prevAbs) abs += 7;
    prevAbs = abs;
    return abs - E4_ABS;
  });
}

function StaffNotation({ progression, sharps }) {
  if (!progression || progression.length === 0) return null;

  const HS     = 4;   // pixels per diatonic half-step
  const STOP   = 24;  // pixels above first staff line (headroom for high notes)
  const CW     = 58;  // pixels per chord column
  const CLW    = 30;  // clef area width
  const NRX    = 5;   // note head rx
  const NRY    = 3.5; // note head ry
  const totalW = CLW + progression.length * CW + 12;
  const totalH = STOP + 8 * HS + 38; // staff + space below for ledger+label

  // y position from relStep (relStep 8 = F5 = top line = y STOP)
  function yR(rel) { return STOP + (8 - rel) * HS; }

  const lineYs = [0, 2, 4, 6, 8].map(yR); // E4, G4, B4, D5, F5

  return (
    <div style={{ overflowX:"auto", borderRadius:10, border:"1px solid #ebebeb", background:"#fff", padding:"6px 0 4px" }}>
      <svg width={totalW} height={totalH} style={{ display:"block" }}>

        {/* Staff lines */}
        {lineYs.map((ly, i) => (
          <line key={i} x1={0} y1={ly} x2={totalW} y2={ly} stroke="#e4e4e4" strokeWidth={1}/>
        ))}

        {/* Treble clef */}
        <text x={4} y={lineYs[0] + 6} fontSize={totalH - 10}
          fontFamily="Times New Roman,Georgia,serif"
          dominantBaseline="alphabetic" fill="#ccc"
          style={{ userSelect:"none", pointerEvents:"none" }}>𝄞</text>

        {/* Chord columns */}
        {progression.map((chord, ci) => {
          const notes = getRootNotes(chord.root, chord.type, chord.type7, chord.use7th, sharps);
          const steps = getStaffSteps(notes);
          const cx    = CLW + ci * CW + CW / 2;
          const fc    = FC[chord.fn] || FC.Tonika;
          const stemX = cx + NRX - 0.5;
          const topY  = yR(Math.max(...steps));
          const botY  = yR(Math.min(...steps));

          return (
            <g key={ci}>
              {/* Thin barline before each chord */}
              <line x1={CLW + ci * CW} y1={lineYs[0]} x2={CLW + ci * CW} y2={lineYs[4]}
                stroke="#e0e0e0" strokeWidth={0.5}/>

              {/* Chord label */}
              <text x={cx} y={totalH - 4} textAnchor="middle"
                fontSize={9} fontFamily="Inter,sans-serif" fontWeight="700" fill={fc.tx}>
                {chord.label}
              </text>

              {/* Stem: from bottom note up through all notes, then 14px above top */}
              <line x1={stemX} y1={botY} x2={stemX} y2={topY - 14}
                stroke={fc.bd} strokeWidth={1.5}/>

              {/* Notes */}
              {steps.map((rel, ni) => {
                const ny  = yR(rel);
                const str = notes[ni] || "";
                const hasSharp = str.includes("#");
                const hasFlat  = str.length > 1 && str[1] === "b";

                return (
                  <g key={ni}>
                    {/* Ledger lines at even relSteps outside the staff */}
                    {rel % 2 === 0 && rel <= -2 && (
                      <line x1={cx - 9} y1={ny} x2={cx + 9} y2={ny} stroke="#aaa" strokeWidth={1}/>
                    )}
                    {rel % 2 === 0 && rel >= 10 && (
                      <line x1={cx - 9} y1={ny} x2={cx + 9} y2={ny} stroke="#aaa" strokeWidth={1}/>
                    )}

                    {/* Accidental */}
                    {hasSharp && (
                      <text x={cx - NRX - 5} y={ny + 3} textAnchor="middle"
                        fontSize={8} fontFamily="Inter,sans-serif" fill={fc.tx}>♯</text>
                    )}
                    {hasFlat && (
                      <text x={cx - NRX - 5} y={ny + 3} textAnchor="middle"
                        fontSize={9} fontFamily="Inter,sans-serif" fill={fc.tx}>♭</text>
                    )}

                    {/* Note head */}
                    <ellipse cx={cx} cy={ny} rx={NRX} ry={NRY} fill={fc.bd}/>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Final double barline */}
        <line x1={totalW - 5} y1={lineYs[0]} x2={totalW - 5} y2={lineYs[4]} stroke="#888" strokeWidth={1.5}/>
        <line x1={totalW - 2} y1={lineYs[0]} x2={totalW - 2} y2={lineYs[4]} stroke="#888" strokeWidth={3}/>
      </svg>
    </div>
  );
}

// ─── Main Tab ─────────────────────────────────────────────────────────────────

export default function GeneratorTab({ pendingProg, onPendingConsumed }) {
  const [sel,         setSel]         = useState(CIRCLE[0]);
  const [mode,        setMode]        = useState("major");
  const [use7th,      setUse7th]      = useState(false);
  const [progression, setProgression] = useState([]);
  const [playing,     setPlaying]     = useState(null);
  const [playingAll,  setPlayingAll]  = useState(false);
  const [expanded,     setExpanded]    = useState(null); // index of expanded pill
  const [showNotation, setShowNotation] = useState(false);
  const [saved,        setSaved]       = useState(()=>{ try{return JSON.parse(localStorage.getItem("chordsn_gen")||"[]")}catch{return[]} });
  const [saveName,    setSaveName]    = useState("");
  const [subTab,      setSubTab]      = useState("builder");
  const [bpm,         setBpm]         = useState(80);
  const [pianoReady,  setPianoReady]  = useState(false);
  const timers = useRef([]);

  // Load cadence from Skalen-Tab
  useEffect(() => {
    if (!pendingProg) return;
    const circle = CIRCLE.find(c => c.key === pendingProg.sel.key);
    if (circle) setSel(circle);
    setMode(pendingProg.mode);
    const isMin = pendingProg.mode === "minor";
    const root  = isMin ? circle.minor.replace("m","") : circle.major;
    const scale = buildScale(root, isMin, circle.sharps);
    const full  = buildChords(scale, isMin, false);
    const prog  = pendingProg.chords.map(c => {
      const match = full.find(fc => fc.degree === c.degree) || c;
      return { ...match, inversion: 0, use7th: false };
    });
    setProgression(prog);
    setSubTab("builder");
    setExpanded(null);
    stopAll();
    onPendingConsumed?.();
  }, [pendingProg]); // eslint-disable-line

  // Preload Salamander samples when tab opens
  useState(() => {
    preloadPiano();
    // Check readiness every 500ms
    const check = setInterval(() => {
      import("./audio").then(m => {
        if (m.isReady && m.isReady()) {
          setPianoReady(true);
          clearInterval(check);
        }
      });
    }, 500);
    setTimeout(() => { setPianoReady(true); clearInterval(check); }, 8000);
    return () => clearInterval(check);
  });


  const isMinor   = mode==="minor";
  const root      = isMinor ? sel.minor.replace("m","") : sel.major;
  const scale     = buildScale(root, isMinor, sel.sharps);
  const allChords = buildChords(scale, isMinor, use7th);
  const suggestions = getSuggestions(progression, allChords, 4);

  // Last chord's notes for voice-leading recommendations
  const lastChordNotes = progression.length>0 ? getChordNotes(progression[progression.length-1], sel.sharps) : [];

  function addChord(chord) { setProgression(p=>[...p,chord]); }
  function removeChord(idx){ setProgression(p=>p.filter((_,i)=>i!==idx)); setExpanded(null); }
  function moveChord(idx,dir){
    setProgression(p=>{
      const a=[...p],t=idx+dir;
      if(t<0||t>=a.length)return a;
      [a[idx],a[t]]=[a[t],a[idx]]; return a;
    });
  }
  function changeInversion(idx, inv){
    setProgression(p=>p.map((c,i)=>i===idx?{...c,inversion:inv}:c));
  }
  function clearAll(){ setProgression([]); setPlaying(null); setExpanded(null); stopAll(); }

  function playSingle(chord,idx){
    setPlaying(idx);
    playChord(getChordNotes(chord,sel.sharps), 2.0);
    setTimeout(()=>setPlaying(p=>p===idx?null:p), 2100);
  }
  function previewChord(notes){ playChord(notes, 1.5); }

  function playAll(){
    if(progression.length===0)return;
    stopAll(); timers.current.forEach(clearTimeout);
    setPlayingAll(true);
    const chordDur=(60/bpm)*2;
    playProgression(progression.map(c=>getChordNotes(c,sel.sharps)),bpm);
    const ts=progression.map((_,i)=>setTimeout(()=>setPlaying(i),i*chordDur*1000));
    const end=setTimeout(()=>{setPlaying(null);setPlayingAll(false);},progression.length*chordDur*1000+400);
    timers.current=[...ts,end];
  }
  function stopPlay(){ timers.current.forEach(clearTimeout); stopAll(); setPlaying(null); setPlayingAll(false); }

  function saveProgression(){
    if(progression.length===0)return;
    const name=saveName.trim()||`${isMinor?sel.minor:sel.major} ${new Date().toLocaleDateString("de")}`;
    const item={name,key:isMinor?sel.minor:sel.major,mode,chords:progression};
    const upd=[item,...saved]; setSaved(upd);
    try{localStorage.setItem("chordsn_gen",JSON.stringify(upd));}catch{}
    setSaveName("");
  }
  function deleteSaved(idx){ const upd=saved.filter((_,i)=>i!==idx); setSaved(upd); try{localStorage.setItem("chordsn_gen",JSON.stringify(upd));}catch{} }
  function loadSaved(item){
    const c=CIRCLE.find(x=>x.major===item.key||x.minor===item.key);
    if(c){setSel(c);setMode(item.mode);}
    setProgression(item.chords); setSubTab("builder");
  }

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:0 }}>
      <div style={{ marginBottom:18 }}>
        <div style={{ fontSize:26,fontWeight:700,fontFamily:"Inter,sans-serif",color:"#111",letterSpacing:"-0.02em" }}>Chord Generator</div>
        <div style={{ fontSize:11,color:"#bbb",fontFamily:"Inter,sans-serif",marginTop:2,display:"flex",alignItems:"center",gap:8 }}>
          Progressionen bauen, anhören und speichern
          {!pianoReady && (
            <span style={{ fontSize:10,color:"#aed581",fontFamily:"Inter,sans-serif",display:"flex",alignItems:"center",gap:4 }}>
              <span style={{ display:"inline-block",width:7,height:7,borderRadius:"50%",background:"#aed581",animation:"pulse 1s infinite" }}/>
              Piano lädt…
            </span>
          )}
          {pianoReady && (
            <span style={{ fontSize:10,color:"#81c784",fontFamily:"Inter,sans-serif" }}>🎹 Bereit</span>
          )}
        </div>
      </div>

      {/* Sub-tabs */}
      <div style={{ display:"flex",gap:0,borderRadius:10,overflow:"hidden",border:"1px solid #ebebeb",marginBottom:20 }}>
        {[{id:"builder",label:"Builder"},{id:"saved",label:`Gespeichert (${saved.length})`}].map(t=>(
          <button key={t.id} onClick={()=>setSubTab(t.id)} style={{ flex:1,padding:"9px 8px",border:"none",cursor:"pointer",background:subTab===t.id?"#1a1a1a":"#fafafa",color:subTab===t.id?"#fff":"#888",fontFamily:"Inter,sans-serif",fontSize:11,fontWeight:600,borderRight:t.id==="builder"?"1px solid #ebebeb":"none",transition:"all 0.15s" }}>{t.label}</button>
        ))}
      </div>

      {subTab==="saved"&&<SavedList saved={saved} onLoad={loadSaved} onDelete={deleteSaved}/>}

      {subTab==="builder"&&(
        <div style={{ display:"flex",flexDirection:"column",gap:20 }}>

          <KeySelector sel={sel} mode={mode} onSelect={s=>{setSel(s);setProgression([]);}} onMode={m=>{setMode(m);setProgression([]);}}/>

          {/* Scale notation */}
          <div>
            <div style={{ fontSize:10,color:"#bbb",fontFamily:"Inter,sans-serif",fontWeight:700,letterSpacing:"0.06em",marginBottom:8 }}>
              SKALA – {isMinor?sel.minor:sel.major} {isMinor?"Moll":"Dur"}
            </div>
            <ScaleNotation scale={scale} sharps={sel.sharps}/>
          </div>

          {/* 7th toggle */}
          <div style={{ display:"flex",alignItems:"center",gap:8 }}>
            <div style={{ fontSize:10,color:"#bbb",fontFamily:"Inter,sans-serif",fontWeight:700,letterSpacing:"0.06em" }}>AKKORDTYP</div>
            <div style={{ display:"flex",gap:0,borderRadius:8,overflow:"hidden",border:"1px solid #e0e0e0" }}>
              <button onClick={()=>setUse7th(false)} style={{ padding:"5px 14px",border:"none",cursor:"pointer",background:!use7th?"#1a1a1a":"#fafafa",color:!use7th?"#fff":"#888",fontFamily:"Inter,sans-serif",fontSize:11,fontWeight:600,borderRight:"1px solid #e0e0e0",transition:"all 0.15s" }}>Dreiklang</button>
              <button onClick={()=>setUse7th(true)}  style={{ padding:"5px 14px",border:"none",cursor:"pointer",background:use7th?"#1a1a1a":"#fafafa",color:use7th?"#fff":"#888",fontFamily:"Inter,sans-serif",fontSize:11,fontWeight:600,transition:"all 0.15s" }}>7th Chords</button>
            </div>
          </div>

          {/* Palette */}
          <div>
            <div style={{ fontSize:10,color:"#bbb",fontFamily:"Inter,sans-serif",fontWeight:700,letterSpacing:"0.06em",marginBottom:10 }}>STUFENAKKORDE – antippen zum Hinzufügen</div>
            <div style={{ display:"flex",gap:7,flexWrap:"wrap" }}>
              {allChords.map((chord,i)=>(
                <ScaleChordBtn key={i} chord={chord} sharps={sel.sharps}
                  onClick={()=>{
                    const c={...chord,inversion:0};
                    addChord(c);
                    previewChord(getChordNotes(c,sel.sharps));
                  }}/>
              ))}
            </div>
          </div>

          {/* Progression */}
          <div>
            <div style={{ display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10 }}>
              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                <div style={{ fontSize:10,color:"#bbb",fontFamily:"Inter,sans-serif",fontWeight:700,letterSpacing:"0.06em" }}>
                  PROGRESSION {progression.length>0&&`· ${progression.length} Akkorde`}
                </div>
                {progression.length>0&&(
                  <button onClick={()=>setShowNotation(v=>!v)} style={{
                    ...S.pill, padding:"2px 9px", fontSize:9,
                    background: showNotation ? "#1a1a1a" : "#f0f0f0",
                    color: showNotation ? "#fff" : "#888",
                  }}>𝄞 Notation</button>
                )}
              </div>
              {progression.length>0&&<button onClick={clearAll} style={{ ...S.pill,background:"#fee",color:"#e88",padding:"3px 10px",fontSize:10 }}>Leeren</button>}
            </div>
            {progression.length===0?(
              <div style={{ ...S.card,textAlign:"center",color:"#ccc",fontSize:12,fontFamily:"Inter,sans-serif",padding:"28px 14px" }}>Akkorde aus der Palette antippen zum Hinzufügen</div>
            ):(
              <div style={{ display:"flex",gap:8,flexWrap:"wrap",padding:"14px",background:"#f9f9f9",borderRadius:14,border:"1px solid #ebebeb" }}>
                {progression.map((chord,i)=>(
                  <ChordPill key={i} chord={chord} sharps={sel.sharps}
                    isPlaying={playing===i} expanded={expanded===i}
                    onPlay={()=>playSingle(chord,i)}
                    onRemove={()=>removeChord(i)}
                    onMoveLeft={()=>moveChord(i,-1)}
                    onMoveRight={()=>moveChord(i,1)}
                    onToggleExpand={()=>setExpanded(expanded===i?null:i)}
                    onChangeInv={inv=>changeInversion(i,inv)}
                    isFirst={i===0} isLast={i===progression.length-1}/>
                ))}
              </div>
            )}
            {/* Notation view */}
            {progression.length>0&&showNotation&&(
              <div style={{ marginTop:10 }}>
                <StaffNotation progression={progression} sharps={sel.sharps}/>
              </div>
            )}

            {progression.length>0&&(
              <div style={{ display:"flex",gap:8,marginTop:12,alignItems:"center",flexWrap:"wrap" }}>
                <button onClick={playingAll?stopPlay:playAll} style={{ ...S.pill,background:playingAll?"#fee":"#1a1a1a",color:playingAll?"#e88":"#fff",padding:"8px 18px",fontSize:12 }}>
                  {playingAll?"⏹ Stop":"▶ Abspielen"}
                </button>
                <div style={{ display:"flex",gap:6,flex:1,minWidth:160 }}>
                  <input value={saveName} onChange={e=>setSaveName(e.target.value)} placeholder="Name (optional)" style={{ flex:1,padding:"6px 10px",borderRadius:8,border:"1px solid #e0e0e0",fontFamily:"Inter,sans-serif",fontSize:11,outline:"none",background:"#fff",minWidth:0 }}/>
                  <button onClick={saveProgression} style={{ ...S.pill,background:"#e8f5e9",color:"#2e7d32",border:"1px solid #a5d6a7",padding:"6px 12px",fontSize:11 }}>Speichern</button>
                </div>
              </div>
            )}
          </div>

          {/* Metronome */}
          <Metronome bpm={bpm} onBpmChange={setBpm}/>

          {/* Suggestions */}
          <div>
            <div style={{ fontSize:10,color:"#bbb",fontFamily:"Inter,sans-serif",fontWeight:700,letterSpacing:"0.06em",marginBottom:8 }}>VORSCHLÄGE – nächster Akkord</div>
            <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
              {suggestions.map((sug,i)=>{
                const rootNotes=getRootNotes(sug.chord.root,sug.chord.type,sug.chord.type7,use7th,sel.sharps);
                const rec=recommendInversion(lastChordNotes,rootNotes);
                const invNotes=getInversionNotes(rootNotes,rec.inversion);
                return(
                  <SuggestionCard key={i} suggestion={sug} sharps={sel.sharps} use7th={use7th} prevNotes={lastChordNotes}
                    onPreview={()=>previewChord(invNotes)}
                    onAdd={()=>{
                      const c={...sug.chord,use7th,label:use7th?sug.chord.label7:sug.chord.label3,inversion:rec.inversion};
                      addChord(c); previewChord(invNotes);
                    }}/>
                );
              })}
            </div>
          </div>

        </div>
      )}
    </div>
  );
}
